
import Component from "vue-class-component";
import Vue from "vue";
import ApplicationHeader from "@/features/applications/components/ApplicationHeader.vue";
import {Prop} from "vue-property-decorator";
import {
    ApplicationActions, ApplicationsClient,
    ApplicationState,
    ApplicationType,
    ApplicationViewModel, 
    CertificatesClient, 
    DocumentDuplicateDetailsModel,
    EndorsementsClient,
    EndorsementDocumentApiModel,
    factory,
    InlandVesselDocumentApiModel,
    IssueDocumentClient,
    IssueDocumentDetailsApiModel,
    IssueDocumentRequest,
    PersonalInfoRequirementValueViewModel,
    PersonalInfoRequirementViewModel,
    ResultError,
    SeagoingShipDocumentApiModel,
    PilotCertificateDocumentApiModel,
    ConcessionPermitDocumentApiModel,
    PilotlessSeafaringDocumentApiModel,
    HarbourPilotExemptionCertificateDocumentApiModel,
    MaritimeCertificateDocumentApiModel,
    MaritimePilotCertificateDocumentApiModel,
    SeniorMaritimePilotCertificateDocumentApiModel,
    PortPilotCertificateDocumentApiModel,
    OpenSeaPilotCertificateDocumentApiModel,
    FastFerryMasterCertificateDocumentApiModel,
    FastFerryEndorsementDocumentApiModel
} from "@/api-client";
import IssueDocumentAsideActions from "@/features/applications/components/issue-document/IssueDocumentAsideActions.vue";
import IssueDocumentInnerComponent from "@/features/applications/components/issue-document/IssueDocumentInnerComponent";
import IssueSeagoingCertificate from "@/features/applications/components/issue-document/IssueSeagoingCertificate.vue";
import IssueInlandVesselCertificate from "@/features/applications/components/issue-document/IssueInlandVesselCertificate.vue";
import IssuePilotCertificate from "@/features/applications/components/issue-document/IssuePilotCertificate.vue";
import IssueMaritimePilotCertificate from "@/features/applications/components/issue-document/IssueMaritimePilotCertificate.vue";
import IssueSeniorMaritimePilotCertificate from "@/features/applications/components/issue-document/IssueSeniorMaritimePilotCertificate.vue";
import IssuePortPilotCertificate from "@/features/applications/components/issue-document/IssuePortPilotCertificate.vue";
import IssueOpenSeaPilotCertificate from "@/features/applications/components/issue-document/IssueOpenSeaPilotCertificate.vue";
import IssueFastFerryMasterCertificate from "@/features/applications/components/issue-document/IssueFastFerryMasterCertificate.vue";
import IssueConcessionPermit from "@/features/applications/components/issue-document/IssueConcessionPermit.vue";
import IssuePilotlessSeafaring from "@/features/applications/components/issue-document/IssuePilotlessSeafaring.vue";
import IssueHarbourPilotExemptionCertificate from "@/features/applications/components/issue-document/IssueHarbourPilotExemptionCertificate.vue";
import IssueMaritimeCertificate from "@/features/applications/components/issue-document/IssueMaritimeCertificate.vue";
import ConfirmWithCommentModal from "@/features/shared/components/ConfirmWithCommentModal.vue";
import ConfirmModal from "@/features/shared/components/ConfirmModal.vue";
import IssueEndorsement from "@/features/applications/components/issue-document/IssueEndorsement.vue";
import IssueFastFerryEndorsement from "@/features/applications/components/issue-document/IssueFastFerryEndorsement.vue";
import toasts from "@/components/Toasts";
import IssueDocumentDuplicate from "@/features/applications/components/issue-document/IssueDocumentDuplicate.vue";
import ApplicationOverviewLayout from "@/features/applications/components/ApplicationOverviewLayout.vue";
import ErrorsSummary from "@/components/ErrorsSummary.vue";
import waitForAllImagesLoaded from "@/components/WaitForImagesLoaded";
import {renderApplicationCertificatePrintPreviewTemplate, renderApplicationEndorsementPrintPreviewTemplate} from "@/features/shared/ApplicationPrintPreviewService";
import renderCertificatePrintTemplate from "@/features/shared/CertificatePrintService";
import renderEndorsementPrintTemplate from "@/features/shared/EndorsementPrintService";
import emptyImage from "@/assets/placeholders/single-pixel.gif";

@Component({
    components: {
        ApplicationHeader,
        IssueDocumentAsideActions,
        IssueSeagoingCertificate,
        IssueInlandVesselCertificate,
        IssuePilotCertificate,
        IssueMaritimePilotCertificate,
        IssueSeniorMaritimePilotCertificate,
        IssuePortPilotCertificate,
        IssueOpenSeaPilotCertificate,
        IssueFastFerryMasterCertificate,
        IssueConcessionPermit,
        IssuePilotlessSeafaring,
        IssueHarbourPilotExemptionCertificate,
        IssueMaritimeCertificate,
        IssueDocumentDuplicate,
        ConfirmWithCommentModal,
        IssueEndorsement,
        IssueFastFerryEndorsement,
        ConfirmModal,
        ApplicationOverviewLayout,
        ErrorsSummary
    }
})
export default class IssueDocumentOverview extends Vue {
    @Prop({required: true})
    application!: ApplicationViewModel
    @Prop({required: true})
    actions!: ApplicationActions;

    issueDocumentDetails: IssueDocumentDetailsApiModel | null = null;
    errors: ResultError = factory.create(ResultError);

    innerCoreComponentName!: string;
    $refs!: {
        innerCoreComponent: IssueDocumentInnerComponent,
        confirmIssueDocumentWithComment: ConfirmWithCommentModal,
        confirmIssueDocumentWithoutComment: ConfirmModal
    }

    async created() {
        const hasDocumentBeenIssued = this.application.state === ApplicationState.DocumentIssued && !this.application.isInEditMode;
        if (hasDocumentBeenIssued) {
            // Redirect away if document has already been issued.
            await this.$router.replace({name: "ApplicationOverview"})
            return;
        }

        this.issueDocumentDetails = await new IssueDocumentClient().details(this.application.id);
        if (this.issueDocumentDetails instanceof FastFerryMasterCertificateDocumentApiModel) {
            this.innerCoreComponentName = "IssueFastFerryMasterCertificate";
        } else if (this.issueDocumentDetails instanceof SeagoingShipDocumentApiModel) {
            this.innerCoreComponentName = "IssueSeagoingCertificate";
        } else if (this.issueDocumentDetails instanceof InlandVesselDocumentApiModel) {
            this.innerCoreComponentName = "IssueInlandVesselCertificate";
        } else if (this.issueDocumentDetails instanceof SeniorMaritimePilotCertificateDocumentApiModel) {
            this.innerCoreComponentName = "IssueSeniorMaritimePilotCertificate";
        } else if (this.issueDocumentDetails instanceof MaritimePilotCertificateDocumentApiModel) {
            this.innerCoreComponentName = "IssueMaritimePilotCertificate";
        } else if (this.issueDocumentDetails instanceof PortPilotCertificateDocumentApiModel) {
            this.innerCoreComponentName = "IssuePortPilotCertificate";
        } else if (this.issueDocumentDetails instanceof OpenSeaPilotCertificateDocumentApiModel) {
            this.innerCoreComponentName = "IssueOpenSeaPilotCertificate";
        } else if (this.issueDocumentDetails instanceof PilotCertificateDocumentApiModel) {
            this.innerCoreComponentName = "IssuePilotCertificate";
        } else if (this.issueDocumentDetails instanceof ConcessionPermitDocumentApiModel) {
            this.innerCoreComponentName = "IssueConcessionPermit";
        } else if (this.issueDocumentDetails instanceof HarbourPilotExemptionCertificateDocumentApiModel) {
            this.innerCoreComponentName = "IssueHarbourPilotExemptionCertificate";
        } else if (this.issueDocumentDetails instanceof PilotlessSeafaringDocumentApiModel) {
            this.innerCoreComponentName = "IssuePilotlessSeafaring";
        } else if (this.issueDocumentDetails instanceof MaritimeCertificateDocumentApiModel) {
            this.innerCoreComponentName = "IssueMaritimeCertificate";
        } else if (this.issueDocumentDetails instanceof FastFerryEndorsementDocumentApiModel) {
            this.innerCoreComponentName = "IssueFastFerryEndorsement";
        } else if (this.issueDocumentDetails instanceof EndorsementDocumentApiModel) {
            this.innerCoreComponentName = "IssueEndorsement";
        } else if (this.issueDocumentDetails instanceof DocumentDuplicateDetailsModel) {
            this.innerCoreComponentName = "IssueDocumentDuplicate";
        } else {
            throw new Error("Unknown document issuing model.");
        }
    }

    async saveDocumentSeed() {
        const request = this.$refs.innerCoreComponent.getEditModel();
        await new IssueDocumentClient()
            .edit(request, this.application.id)
            .then(() => {
                    toasts.success(this.$ls.Applications_IssueDocument_Toast_Success_DocumentSeedSaved);
                    this.$emit("application-changed");
                    if(this.application.isInEditMode){
                        this.$router.push({name: 'ApplicationOverview', params: {applicationId: this.application.id, successMessage: "true"}});
                    }
                },
                error => this.handleErrors(error));
    }

    confirmIssueDocument() {
        if (this.application.areAllMandatoryRequirementsAccepted) {
            this.$refs.confirmIssueDocumentWithoutComment.show()
        } else {
            this.$refs.confirmIssueDocumentWithComment.show()
        }
    }

    async issueDocument(comment: string | null) {
        this.errors = factory.create(ResultError); // Reset errors.

        if (this.$refs.innerCoreComponent.hasEdits()) {
            const request = this.$refs.innerCoreComponent.getEditModel();
            await new IssueDocumentClient()
                .edit(request, this.application.id)
                .then(
                    () => {
                        return;
                    }, // We will emit "changed" event in the next API call.
                    error => this.handleErrors(error));
        }

        if (this.errors.hasMessages()) // This means that save failed.
            return;

        await new IssueDocumentClient()
            .issueDocument(new IssueDocumentRequest({comment: comment}), this.application.id)
            .then(async () => {
                this.$emit("application-changed") // NOTE: This has to be before $router.push.
                await this.$router.push({name: "ApplicationOverview"})
            }, error => this.handleErrors(error));
    }

    async forwardToSigning() {
        this.errors = factory.create(ResultError); // Reset errors.

        if (this.$refs.innerCoreComponent.hasEdits()) {
            const request = this.$refs.innerCoreComponent.getEditModel();
            await new IssueDocumentClient()
                .edit(request, this.application.id)
                .then(
                    () => {
                        return;
                    }, // We will emit "changed" event in the next API call.
                    error => this.handleErrors(error));
        }

        if (this.errors.hasMessages()) // This means that save failed.
            return;

        await new IssueDocumentClient()
            .forwardToSigning(this.application.id)
            .then(async () => {
                this.$emit("application-changed")
            }, error => this.handleErrors(error));
    }

    async undoForwardToSigningClicked() {
        await new IssueDocumentClient()
            .undoForwardToSigning(this.application.id)
            .then(async () => {
                this.$emit("application-changed")
            }, error => this.handleErrors(error));
    }

    async signDocumentSeed() {
        this.errors = factory.create(ResultError); // Reset errors.

        if (this.$refs.innerCoreComponent.hasEdits()) {
            const request = this.$refs.innerCoreComponent.getEditModel();
            await new IssueDocumentClient()
                .edit(request, this.application.id)
                .then(
                    () => {
                        return;
                    }, // We will emit "changed" event in the next API call.
                    error => this.handleErrors(error));
        }

        if (this.errors.hasMessages()) // This means that save failed.
            return;

        await new IssueDocumentClient()
            .sign(this.application.id)
            .then(() => this.$emit("application-changed"),
                error => this.handleErrors(error));
    }

    async removeDocumentSeedSignature() {
        await new IssueDocumentClient()
            .removeSignature(this.application.id)
            .then(() => this.$emit("application-changed"),
                error => this.handleErrors(error));
    }

    async takeOverProcessing() {
        await new ApplicationsClient().takeOverProcessing(this.application.id).then(
            () => this.$emit("application-changed"),
            error => this.handleErrors(error));
    }

    async showPrintPreview(){
        const printWindow = window.open();
        if (!printWindow) {
            return;
        }
        printWindow.document.open();
        printWindow.document.write(await this.getRenderedPrintTemplate());
        printWindow.document.close();
        printWindow.focus();
        await waitForAllImagesLoaded(printWindow);
        printWindow.print();
    }
    
    async getRenderedPrintTemplate() {
        const printTemplateUrl = (await new ApplicationsClient().printTemplate(this.application.id)).url;
        const personalInfoRequirementValue = this.application.requirementFulfillments.find(r => r.requirement instanceof PersonalInfoRequirementViewModel)?.requirementValue as PersonalInfoRequirementValueViewModel;
          
        if(this.application.applicationType && this.application.applicationType == ApplicationType.Duplicate){
            const documentDuplicateDetailsModel = this.issueDocumentDetails as DocumentDuplicateDetailsModel;
            if(this.application.isForCertificate){
                const originalCertificate =  await new CertificatesClient().details(documentDuplicateDetailsModel.documentId);
                originalCertificate.documentNumber = "";
                originalCertificate.officialSignature = null;
                originalCertificate.accessToken = null;
                return await renderCertificatePrintTemplate(originalCertificate, printTemplateUrl, emptyImage);
            }
            else if(this.application.isForEndorsement){
                const originalEndorsement = await new EndorsementsClient().details(documentDuplicateDetailsModel.documentId);
                originalEndorsement.documentNumber = "";
                originalEndorsement.accessToken = null;
                originalEndorsement.officialSignature = null;
                return await renderEndorsementPrintTemplate(originalEndorsement, printTemplateUrl, emptyImage);
            }
        }
        else if(this.application.isForCertificate){
            return await renderApplicationCertificatePrintPreviewTemplate(this.issueDocumentDetails as IssueDocumentDetailsApiModel, personalInfoRequirementValue, this.application.seafarerId, printTemplateUrl);
        }
        else if(this.application.isForEndorsement){
            return await renderApplicationEndorsementPrintPreviewTemplate(this.issueDocumentDetails as EndorsementDocumentApiModel, personalInfoRequirementValue, this.application.seafarerId, printTemplateUrl);
        }
        return "";
    }
    
    handleErrors(error: ResultError) {
        this.setValidationErrors(error, this.$ls.Global_Toast_ActionFailureWithErrorsBelowFields, "errors");
    }
}
